/**
 * Who is signed in, and the token that proves it — whichever provider this
 * build has.
 *
 * The one module the rest of the app imports for a session. Descope wins when
 * both are configured: it is the provider being moved to
 * (`docs/plans/multi-brand-accounts.md`), and a build that answered from two
 * would sign one person in twice. Firebase stays until its users are moved
 * across; with neither, every export is inert and the app runs on
 * `VITE_QV_USER_ID`, as it always has.
 *
 * The functions are re-exported by provider rather than wrapped, so each keeps
 * its own lazy load and nothing here constructs an SDK.
 */

import * as descope from "./descope";
import * as firebase from "./firebase";
import { passHeaders } from "./pass";

export type AuthProvider = "descope" | "firebase" | null;

/** Which provider this build signs in with, fixed at build time. */
export const authProvider: AuthProvider = descope.descopeConfigured
  ? "descope"
  : firebase.firebaseConfigured
    ? "firebase"
    : null;

/** Whether this build can sign anyone in at all. */
export const authConfigured: boolean = authProvider !== null;

const active = authProvider === "descope" ? descope : firebase;

/** See `firebase.ts`: fires once with the answer, then on every change. */
export function onAuthChanged(callback: (uid: string | null) => void): () => void {
  return active.onAuthChanged(callback);
}

/** A current token, or `null`. Never cached here — see `firebase.ts`. */
export function idToken(): Promise<string | null> {
  return active.idToken();
}

/** `Authorization` and the groups pass, or nothing at all — see `firebase.ts`. */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = await idToken();
  return token ? { Authorization: `Bearer ${token}`, ...passHeaders() } : {};
}

export function signOut(): Promise<void> {
  return active.signOut();
}

/**
 * The signed-in account's email, or `null` when nobody is signed in or the
 * provider does not say. Used to pre-fill a field, never to identify anyone —
 * the server's account key does that.
 */
export function signedInEmail(): Promise<string | null> {
  return authConfigured ? active.signedInEmail() : Promise.resolve(null);
}
